import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  LinearProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Stack,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimelineIcon from '@mui/icons-material/Timeline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

function Dashboard() {
  const theme = useTheme();
  const [tabValue, setTabValue] = React.useState(0);

  // Weekly progress data
  const weeklyProgress = [
    { name: 'Mon', hours: 2, progress: 15 },
    { name: 'Tue', hours: 3, progress: 25 },
    { name: 'Wed', hours: 4, progress: 40 },
    { name: 'Thu', hours: 3, progress: 30 },
    { name: 'Fri', hours: 5, progress: 45 },
    { name: 'Sat', hours: 4, progress: 35 },
    { name: 'Sun', hours: 3, progress: 25 },
  ];

  // Course completion data
  const courseCompletion = [
    { name: 'Completed', value: 3 },
    { name: 'In Progress', value: 1 },
    { name: 'Not Started', value: 1 },
  ];

  // Skill assessment data
  const skillAssessment = [
    { subject: 'Patient Care', A: 85, fullMark: 100 },
    { subject: 'Emergency Response', A: 70, fullMark: 100 },
    { subject: 'Medical Knowledge', A: 75, fullMark: 100 },
    { subject: 'Communication', A: 90, fullMark: 100 },
    { subject: 'Documentation', A: 80, fullMark: 100 },
    { subject: 'Safety Protocols', A: 95, fullMark: 100 },
  ];

  // Monthly performance data
  const monthlyPerformance = [
    { name: 'Jan', practical: 85, theoretical: 78 },
    { name: 'Feb', practical: 88, theoretical: 82 },
    { name: 'Mar', practical: 90, theoretical: 85 },
    { name: 'Apr', practical: 92, theoretical: 88 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];
  // Dummy data for the dashboard
  const progressData = {
    overallProgress: 65,
    coursesCompleted: 3,
    totalCourses: 5,
    totalHours: 24,
    certificatesEarned: 2,
    recentActivities: [
      { id: 1, activity: 'Completed Choking Management Course', date: '2025-02-28' },
      { id: 2, activity: 'Started Fall Prevention Course', date: '2025-02-27' },
      { id: 3, activity: 'Earned Basic Life Support Certificate', date: '2025-02-25' },
    ],
    upcomingDeadlines: [
      { id: 1, course: 'Medication Management', deadline: '2025-03-15' },
      { id: 2, course: 'Emergency Response', deadline: '2025-03-20' },
    ],
    courseProgress: [
      { course: 'Choking Management', progress: 100 },
      { course: 'Fall Prevention', progress: 45 },
      { course: 'Basic Life Support', progress: 100 },
      { course: 'Medication Management', progress: 20 },
      { course: 'Emergency Response', progress: 0 },
    ],
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          fontWeight: 500, 
          color: theme.palette.text.primary,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          pb: 1
        }}>
          Training Analytics Dashboard
        </Typography>
      </Box>


      <Grid container spacing={3}>
        {/* Overall Progress Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                Overall Progress
              </Typography>
              <Typography variant="h4" sx={{ color: 'primary.contrastText' }}>
                {progressData.overallProgress}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimelineIcon sx={{ color: 'primary.contrastText' }} />
                <Typography variant="body2" sx={{ ml: 1, color: 'primary.contrastText' }}>
                  On track
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Courses Completed
              </Typography>
              <Typography variant="h4" color="text.primary">
                {progressData.coursesCompleted}/{progressData.totalCourses}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon color="success" />
                <Typography variant="body2" color="success.main" sx={{ ml: 1 }}>
                  +2 this month
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Training Hours
              </Typography>
              <Typography variant="h4" color="text.primary">
                {progressData.totalHours}h
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon color="info" />
                <Typography variant="body2" color="info.main" sx={{ ml: 1 }}>
                  5h this week
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Skills Acquired
              </Typography>
              <Typography variant="h4" color="text.primary">
                {progressData.skillsAcquired}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmojiEventsIcon color="warning" />
                <Typography variant="body2" color="warning.main" sx={{ ml: 1 }}>
                  3 new skills
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Analytics Tabs */}
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  minHeight: 64,
                }
              }}
            >
              <Tab label="Progress Overview" />
              <Tab label="Skill Assessment" />
              <Tab label="Performance Metrics" />
            </Tabs>

            {/* Progress Overview Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: 400,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>Weekly Progress</Typography>
                    <Box sx={{ height: '80%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={weeklyProgress}
                          margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis 
                            width={40} 
                            axisLine={false} 
                            tickLine={false} 
                            tickCount={6} 
                            domain={[0, 'dataMax + 10']} 
                            padding={{ top: 10 }}
                          />
                          <Tooltip />
                          <Area
                            type="monotone"
                            dataKey="progress"
                            stroke={theme.palette.primary.main}
                            fill={theme.palette.primary.light}
                          />
                          <Area
                            type="monotone"
                            dataKey="hours"
                            stroke={theme.palette.secondary.main}
                            fill={theme.palette.secondary.light}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.primary.light, border: `1px solid ${theme.palette.primary.main}`, mr: 1 }} />
                        <Typography variant="body2">Progress (%)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.secondary.light, border: `1px solid ${theme.palette.secondary.main}`, mr: 1 }} />
                        <Typography variant="body2">Hours</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: 400,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>Course Status</Typography>
                    <Box sx={{ height: '80%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={courseCompletion}
                            cx="40%"
                            cy="40%"
                            innerRadius={60}
                            outerRadius={80}
                            fill="#8884d8"
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {courseCompletion.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      {courseCompletion.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                          <Box sx={{ width: 12, height: 12, backgroundColor: COLORS[index % COLORS.length], mr: 1 }} />
                          <Typography variant="body2">{entry.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Skill Assessment Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: 400,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>Skill Competency</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={skillAssessment}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar
                          name="Skills"
                          dataKey="A"
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          fillOpacity={0.6}
                        />
                        <Tooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: 400,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>Key Skills</Typography>
                    <Box sx={{ px: 2, height: 350, overflowY: 'auto' }}>
                      {skillAssessment.map((skill) => (
                        <Box key={skill.subject} sx={{ mb: 2.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>{skill.subject}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {skill.A}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={skill.A}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: theme.palette.grey[200],
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: theme.palette.primary.main
                              }
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Performance Metrics Tab */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: 400,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>Monthly Performance</Typography>
                    <Box sx={{ height: '80%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={monthlyPerformance}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="practical" fill={theme.palette.primary.main} />
                          <Bar dataKey="theoretical" fill={theme.palette.secondary.main} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.primary.main, mr: 1 }} />
                        <Typography variant="body2">Practical</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.secondary.main, mr: 1 }} />
                        <Typography variant="body2">Theoretical</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </Paper>
        </Grid>

        {/* Course Progress Section */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
              Course Progress
            </Typography>
            <Stack spacing={3}>
              {progressData.courseProgress.map((course) => (
                <Box key={course.course}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {course.course}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {course.progress}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={course.progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Recent Activities & Deadlines */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Recent Activities */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                Recent Activities
              </Typography>
              <List>
                {progressData.recentActivities.map((activity) => (
                  <React.Fragment key={activity.id}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemIcon>
                        <AssignmentIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.activity}
                        secondary={activity.date}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </Paper>

            {/* Upcoming Deadlines */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                Upcoming Deadlines
              </Typography>
              <List>
                {progressData.upcomingDeadlines.map((deadline) => (
                  <React.Fragment key={deadline.id}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemIcon>
                        <AccessTimeIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={deadline.course}
                        secondary={`Due by ${deadline.deadline}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Dashboard;
